import React from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import HierarchyTreeItem from "./hierarchy-tree-item";
import HierarchyTreeTooltip from "./hierarchy-tree-tooltip";
import { DragWrapper, DropWrapper } from "../drag-and-drop";
import { alwaysOpenBranch, organisationalUnit, rootOption } from "./constants";
import "./hierarchy-tree.style.scss";

class HierarchyTree extends React.Component {
  constructor(props) {
    super(props);
    this.functions = this.initialiseFunctions(props.options);
    this.ref = {};
    this.state = {
      openBranches: { [props.tree.data.id || -1]: true },
    };
    this.handleOpenBranches = event => {
      event.stopPropagation();
      const id = event.currentTarget.dataset.id;
      this.setState(
        Object.assign({}, this.state, {
          openBranches: Object.assign({}, this.state.openBranches, {
            [id]: !this.state.openBranches[id],
          }),
          openTooltip: null,
        }),
      );
    };
    this.handleOpenTooltips = event => {
      event.stopPropagation();
      this.setState({
        openTooltip:
          +event.currentTarget.dataset.id === this.state.openTooltip
            ? null
            : +event.currentTarget.dataset.id,
      });
    };
    this.closeToolTip = event => {
      this.setState({ openTooltip: null });
    };
    this.exploreDOM = (target, targetClass) => {
      return (
        (target.classList && target.classList.contains(targetClass)) ||
        (target.parentNode && this.exploreDOM(target.parentNode, targetClass))
      );
    };
    this.handleClicksOutside = event => {
      if (!this.exploreDOM(event.target, "tooltip-inner")) {
        this.closeToolTip();
      }
    };
  }

  componentDidMount() {
    document.body.addEventListener("click", this.handleClicksOutside);
  }

  componentWillUnmount() {
    document.body.removeEventListener("click", this.handleClicksOutside);
  }

  initialiseFunctions(options) {
    const functions = {};
    options.forEach(option => {
      functions[option.name] = event => {
        if (
          alwaysOpenBranch.includes(option.name) &&
          !this.state.openBranches[event.target.dataset.id]
        ) {
          this.setState({
            openBranches: Object.assign({}, this.state.openBranches, {
              [event.target.dataset.id]: true,
            }),
          });
        }
        this.closeToolTip();
        this.props[option.name](event.target.dataset.id);
      };
    });
    return functions;
  }

  parseTree(tree, layer = 0) {
    const isOpen = tree.data.id === this.state.openTooltip;
    const isNotLeaf = Boolean(tree.children.length);
    const employees = tree.children.reduce(
      (acc, child) => acc + +(child.data.type !== organisationalUnit),
      0,
    );
    const units = tree.children.length - employees;
    if (!layer && !this.props.isBranch) {
      //root case: droppable, not draggable, partial tooltip' not valid when tree is only branch
      return (
        <div>
          <DropWrapper className="w-100" name={tree.data.id} onClick={this.handleOpenTooltips}>
            <HierarchyTreeItem
              indentSize={this.props.indentSize}
              isNotLeaf={isNotLeaf}
              keyId={tree.data.id}
              layer={layer}
              onIconClick={this.handleOpenBranches}
              name={tree.data.label}
              plus={!this.state.openBranches[tree.data.id]}
              style={{ minHeight: `${this.props.indentSize}px` }}
              tooltipAncor
            />
            <HierarchyTreeTooltip
              employees={employees}
              employeesText={this.props.employeesText}
              functions={this.functions}
              keyId={tree.data.id}
              isOpen={isOpen}
              isNotLeaf={isNotLeaf}
              options={this.props.options.filter(option => option.name === rootOption)}
              units={units}
              unitsText={this.props.unitsText}
            />
          </DropWrapper>
          <div className={`collapse${this.state.openBranches[tree.data.id] ? " show" : ""}`}>
            {isNotLeaf && tree.children.map(child => this.parseTree(child, layer + 1))}
          </div>
        </div>
      );
    }
    return (
      !this.props.bufferedBranches[tree.data.id] &&
      (this.props.options.length && tree.data.type === organisationalUnit ? (
        <DragWrapper
          className="w-100"
          key={tree.data.id}
          name={tree.data.id}
          onDrop={this.props.onDrop}
        >
          <DropWrapper className="w-100" name={tree.data.id} onClick={this.handleOpenTooltips}>
            <HierarchyTreeItem
              indentSize={this.props.indentSize}
              isNotLeaf={isNotLeaf}
              keyId={tree.data.id}
              layer={layer}
              onIconClick={this.handleOpenBranches}
              name={tree.data.label}
              plus={!this.state.openBranches[tree.data.id]}
              style={{ minHeight: `${this.props.indentSize}px` }}
              tooltipAncor
            />
            {this.props.options.length && (
              <HierarchyTreeTooltip
                employees={employees}
                employeesText={this.props.employeesText}
                functions={this.functions}
                keyId={tree.data.id}
                isOpen={isOpen}
                isNotLeaf={isNotLeaf}
                options={this.props.options}
                units={units}
                unitsText={this.props.unitsText}
              />
            )}
          </DropWrapper>
          <div className={`collapse${this.state.openBranches[tree.data.id] ? " show" : ""}`}>
            {isNotLeaf && tree.children.map(child => this.parseTree(child, layer + 1))}
          </div>
        </DragWrapper>
      ) : (
        <div className="w-100" key={tree.data.id}>
          <HierarchyTreeItem
            indentSize={this.props.indentSize}
            isNotLeaf={isNotLeaf}
            keyId={this.props.options.length ? undefined : tree.data.id}
            layer={layer}
            onIconClick={this.handleOpenBranches}
            name={tree.data.label}
            plus={!this.state.openBranches[tree.data.id]}
            style={{ minHeight: `${this.props.indentSize}px` }}
          />
          <div className={`collapse${this.state.openBranches[tree.data.id] ? " show" : ""}`}>
            {isNotLeaf && tree.children.map(child => this.parseTree(child, layer + 1))}
          </div>
        </div>
      ))
    );
  }

  render() {
    return (
      <div className={classNames("hierarchy-tree-default w-100", this.props.className)}>
        {this.parseTree(this.props.tree)}
      </div>
    );
  }
}

HierarchyTree.propTypes = {
  add: PropTypes.func,
  unitsText: PropTypes.string,
  bufferedBranches: PropTypes.object,
  className: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.object,
  ]),
  cut: PropTypes.func,
  delete: PropTypes.func,
  indentSize: PropTypes.number,
  isBranch: PropTypes.bool,
  employeesText: PropTypes.string,
  onDrop: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      text: PropTypes.string,
    }),
  ),
  rename: PropTypes.func,
  tree: PropTypes.object,
  viewScopeChanges: PropTypes.func,
};

HierarchyTree.defaultProps = {
  unitsText: "Units",
  indentSize: 16,
  employeesText: "Employees in Unit",
  options: [],
  bufferedBranches: {},
};

export default HierarchyTree;
